/**
 * Name Display Utilities
 * ======================
 * Helper functions for displaying member names throughout the app.
 * Privacy requirement: Only show first names to other members.
 */

/**
 * Extract first name from a full name string.
 * Handles various name formats safely.
 * 
 * @param {string} fullName - The user's full name
 * @returns {string} - First name only
 * 
 * @example
 * getFirstName("John Smith") // "John"
 * getFirstName("Mary Jane Watson") // "Mary"
 * getFirstName("Prince") // "Prince"
 * getFirstName("") // "Member"
 * getFirstName(null) // "Member"
 */
export const getFirstName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return 'Member';
  }
  
  const trimmed = fullName.trim();
  if (!trimmed) {
    return 'Member';
  }
  
  // Split by space and take first part
  const parts = trimmed.split(/\s+/);
  return parts[0] || 'Member';
};

/**
 * Get display name for a user - first name only for privacy.
 * Falls back to 'Member' if no name available.
 * 
 * @param {Object} user - User object with name property
 * @returns {string} - First name for display
 */
export const getDisplayName = (user) => {
  if (!user) return 'Member';
  return getFirstName(user.name || user.firstName || user.first_name);
};

/**
 * Format name with age for display (e.g., "Alex, 28")
 * Uses first name only.
 * 
 * @param {Object} user - User object
 * @param {boolean} showAge - Whether to include age
 * @returns {string} - Formatted display string
 */
export const formatNameWithAge = (user, showAge = true) => {
  const firstName = getDisplayName(user);
  
  if (showAge && user && user.age) {
    return `${firstName}, ${user.age}`;
  }
  
  return firstName;
};

/**
 * Get initials from first name (for avatars)
 * 
 * @param {string} name - User's name
 * @returns {string} - Single initial letter
 */
export const getInitial = (name) => {
  const firstName = getFirstName(name);
  return firstName.charAt(0).toUpperCase();
};

export default {
  getFirstName,
  getDisplayName,
  formatNameWithAge,
  getInitial
};
